import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import HRDashboard from "./pages/hr/HRDashboard";
import EmployeeDashboard from "./pages/employee/EmployeeDashboard";
import PsychometricsPage from "./pages/employee/PsychometricsPage";
import VoiceAnalysisPage from "./pages/hr/VoiceAnalysisPage";
import AlertsPage from "./pages/hr/AlertsPage";
import EmployeesPage from "./pages/hr/EmployeesPage";
import AnalyticsPage from "./pages/hr/AnalyticsPage";
import SurveysPage from "./pages/SurveysPage";
import WellbeingPage from "./pages/WellbeingPage";
import PlatformDashboard from "./pages/platform/PlatformDashboard";

// Layout
import Layout from "./components/Layout";

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9]">
                <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (requiredRole === 'platform_owner' && user.role !== 'platform_owner') {
        return <Navigate to="/dashboard" replace />;
    }

    if (requiredRole === 'hr' && !['hr', 'admin', 'platform_owner'].includes(user.role)) {
        return <Navigate to="/employee" replace />;
    }

    return <Layout>{children}</Layout>;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9]">
                <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
            </div>
        );
    }

    if (user) {
        if (user.role === 'platform_owner') {
            return <Navigate to="/platform" replace />;
        }
        return <Navigate to={user.role === 'hr' || user.role === 'admin' ? '/dashboard' : '/employee'} replace />;
    }

    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />

            {/* Platform Owner Routes */}
            <Route path="/platform" element={<ProtectedRoute requiredRole="platform_owner"><PlatformDashboard /></ProtectedRoute>} />

            {/* HR/Admin Routes */}
            <Route path="/dashboard" element={<ProtectedRoute requiredRole="hr"><HRDashboard /></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute requiredRole="hr"><AnalyticsPage /></ProtectedRoute>} />
            <Route path="/employees" element={<ProtectedRoute requiredRole="hr"><EmployeesPage /></ProtectedRoute>} />
            <Route path="/alerts" element={<ProtectedRoute requiredRole="hr"><AlertsPage /></ProtectedRoute>} />
            <Route path="/voice" element={<ProtectedRoute requiredRole="hr"><VoiceAnalysisPage /></ProtectedRoute>} />
            <Route path="/surveys" element={<ProtectedRoute requiredRole="hr"><SurveysPage /></ProtectedRoute>} />
            <Route path="/wellbeing" element={<ProtectedRoute requiredRole="hr"><WellbeingPage /></ProtectedRoute>} />

            {/* Employee Routes */}
            <Route path="/employee" element={<ProtectedRoute><EmployeeDashboard /></ProtectedRoute>} />
            <Route path="/employee/wellbeing" element={<ProtectedRoute><WellbeingPage /></ProtectedRoute>} />
            <Route path="/employee/psychometrics" element={<ProtectedRoute><PsychometricsPage /></ProtectedRoute>} />
            <Route path="/employee/surveys" element={<ProtectedRoute><SurveysPage /></ProtectedRoute>} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <AppRoutes />
                <Toaster position="top-right" />
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
